import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import DefaultData from "../../CompanyData/DefaultData";
import Header from "../../Common/Layout/Header";
import Modal from "react-bootstrap/Modal";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import { format } from "date-fns";
import AccountTabs from "../Accounts/AccountTabs";

const TaxRules = () => {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    // State for tax rules list
    const [taxRules, setTaxRules] = useState([]);
    const [loading, setLoading] = useState(true);

    // Global Taxes and Tax Collections for dropdowns
    const [globalTaxes, setGlobalTaxes] = useState([]);
    const [taxCollections, setTaxCollections] = useState([]);

    // Modal states
    const [editShow, setEditShow] = useState(false);
    const [editTaxRule, setEditTaxRule] = useState({
        taxRuleId: null,
        companyId: DefaultData.DEFAULTCOMPANYID, // Set default company ID
        globalTaxId: "",
        taxCollectionId: null,
        serviceCategoryId: "",
        ruleName: "",
        customerType: "",
        applyOnBaseFare: false,
        applyOnMarkup: false,
        applyOnServiceCharge: false,
        applyOnCommission: false,
        isActive: true,
        effectiveFrom: new Date().toISOString().slice(0, 16),
        effectiveTo: null,
        description: "",
    });
    const [editBtnLoading, setEditBtnLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    // Options for dropdowns
    const serviceCategories = [
        "FLIGHT",
        "HOTEL",
        "PACKAGE",
        "VISA",
        "BUS",
        "TRAIN",
        "CAR",
        "TRANSFER",
        "SIGHTSEEING",
        "COMMISSION",
        "OTHER",
    ];

    const customerTypes = ["Portal", "B2B", "B2C", "Corporate"];

    // Get all tax rules
    const getTaxRules = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            const response = await apiService.getAll(config.GETALLTAXRULES);
            const data = response.data || response || [];
            setTaxRules(data);
        } catch (error) {
            console.error("Error fetching tax rules:", error);
            setTaxRules([]);
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Get global taxes for dropdown
    const getGlobalTaxes = useCallback(async () => {
        try {
            const response = await apiService.getAll(config.GETALLGLOBALTAXES);
            const data = response.data || response || [];
            setGlobalTaxes(data);
        } catch (error) {
            console.error("Error fetching global taxes:", error);
            setGlobalTaxes([]);
        }
    }, []);

    // Get tax collections for dropdown
    const getTaxCollections = useCallback(async () => {
        try {
            const response = await apiService.getAll(config.GETALLTAXCOLLECTIONS);
            const data = response.data || response || [];
            setTaxCollections(data);
        } catch (error) {
            console.error("Error fetching tax collections:", error);
            setTaxCollections([]);
        }
    }, []);

    useEffect(() => {
        if (!authLoading && user) {
            getTaxRules();
            getGlobalTaxes();
            getTaxCollections();
        }
    }, [user, authLoading, getTaxRules, getGlobalTaxes, getTaxCollections]);

    // Handle Add Tax Rule
    const handleAddTaxRule = () => {
        setEditTaxRule({
            taxRuleId: null,
            companyId: DefaultData.DEFAULTCOMPANYID,
            globalTaxId: "",
            taxCollectionId: null,
            serviceCategoryId: "",
            ruleName: "",
            customerType: "",
            applyOnBaseFare: false,
            applyOnMarkup: false,
            applyOnServiceCharge: false,
            applyOnCommission: false,
            isActive: true,
            effectiveFrom: new Date().toISOString().slice(0, 16),
            effectiveTo: null,
            description: "",
        });
        setFormErrors({});
        setEditShow(true);
    };

    // Handle Edit Tax Rule
    const handleEditClick = (taxRule) => {
        const editData = {
            ...taxRule,
            taxRuleId: taxRule.taxRuleId,
            effectiveFrom: taxRule.effectiveFrom
                ? new Date(taxRule.effectiveFrom).toISOString().slice(0, 16)
                : new Date().toISOString().slice(0, 16),
            effectiveTo: taxRule.effectiveTo
                ? new Date(taxRule.effectiveTo).toISOString().slice(0, 16)
                : null,
            globalTaxId: taxRule.globalTaxId || "",
            taxCollectionId: taxRule.taxCollectionId || null,
            companyId: taxRule.companyId || DefaultData.DEFAULTCOMPANYID,
        };
        setEditTaxRule(editData);
        setFormErrors({});
        setEditShow(true);
    };

    // Close edit modal
    const handleEditClose = () => {
        setEditShow(false);
        setEditTaxRule({
            taxRuleId: null,
            companyId: DefaultData.DEFAULTCOMPANYID,
            globalTaxId: "",
            taxCollectionId: null,
            serviceCategoryId: "",
            ruleName: "",
            customerType: "",
            applyOnBaseFare: false,
            applyOnMarkup: false,
            applyOnServiceCharge: false,
            applyOnCommission: false,
            isActive: true,
            effectiveFrom: "",
            effectiveTo: null,
            description: "",
        });
        setFormErrors({});
        setEditBtnLoading(false);
    };

    // Handle input change
    const handleEditInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Handle checkbox inputs
        if (type === "checkbox") {
            setEditTaxRule({
                ...editTaxRule,
                [name]: checked,
            });
        } else {
            setEditTaxRule({
                ...editTaxRule,
                [name]: value,
            });
        }

        // Clear error for this field
        if (formErrors[name]) {
            setFormErrors({ ...formErrors, [name]: null });
        }

        // If service category changes to FLIGHT, auto-set validation rules
        if (name === "serviceCategoryId" && value === "FLIGHT") {
            setEditTaxRule(prev => ({
                ...prev,
                serviceCategoryId: value,
                applyOnBaseFare: false,
                applyOnCommission: true,
            }));
        }
    };

    // Handle select change for dropdowns
    const handleSelectChange = (e) => {
        const { name, value } = e.target;

        // Convert string to boolean for isActive
        if (name === "isActive") {
            setEditTaxRule({
                ...editTaxRule,
                [name]: value === "true",
            });
        } else {
            setEditTaxRule({
                ...editTaxRule,
                [name]: value,
            });
        }

        // Clear error for this field
        if (formErrors[name]) {
            setFormErrors({ ...formErrors, [name]: null });
        }

        // If service category changes to FLIGHT, auto-set validation rules
        if (name === "serviceCategoryId" && value === "FLIGHT") {
            setEditTaxRule(prev => ({
                ...prev,
                serviceCategoryId: value,
                applyOnBaseFare: false,
                applyOnCommission: true,
            }));
        }
    };

    // Validate form
    const validateForm = () => {
        const errors = {};

        if (!editTaxRule.globalTaxId) {
            errors.globalTaxId = "Global Tax is required";
        }

        if (!editTaxRule.serviceCategoryId) {
            errors.serviceCategoryId = "Service Category is required";
        }

        if (!editTaxRule.ruleName || editTaxRule.ruleName.trim() === "") {
            errors.ruleName = "Rule Name is required";
        }

        if (!editTaxRule.customerType) {
            errors.customerType = "Customer Type is required";
        }

        if (!editTaxRule.effectiveFrom) {
            errors.effectiveFrom = "Effective from date is required";
        }

        if (editTaxRule.effectiveFrom && editTaxRule.effectiveTo) {
            const fromDate = new Date(editTaxRule.effectiveFrom);
            const toDate = new Date(editTaxRule.effectiveTo);
            if (toDate <= fromDate) {
                errors.effectiveTo = "Effective to date must be after effective from date";
            }
        }

        // Validation for Flights
        if (editTaxRule.serviceCategoryId === "FLIGHT") {
            if (editTaxRule.applyOnBaseFare) {
                errors.applyOnBaseFare = "For Flights, Apply On Base Fare must be FALSE";
            }
            if (!editTaxRule.applyOnCommission) {
                errors.applyOnCommission = "For Flights, Apply On Commission must be TRUE";
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle Save Tax Rule
    const handleSaveTaxRule = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setEditBtnLoading(true);
        try {
            // Use DEFAULTCOMPANYID if companyId is empty
            const companyId = editTaxRule.companyId || DefaultData.DEFAULTCOMPANYID;

            const taxRuleData = {
                companyId: companyId,
                globalTaxId: editTaxRule.globalTaxId,
                taxCollectionId: editTaxRule.taxCollectionId || null,
                serviceCategoryId: editTaxRule.serviceCategoryId,
                ruleName: editTaxRule.ruleName.trim(),
                customerType: editTaxRule.customerType,
                applyOnBaseFare: editTaxRule.applyOnBaseFare,
                applyOnMarkup: editTaxRule.applyOnMarkup,
                applyOnServiceCharge: editTaxRule.applyOnServiceCharge,
                applyOnCommission: editTaxRule.applyOnCommission,
                isActive: editTaxRule.isActive,
                effectiveFrom: new Date(editTaxRule.effectiveFrom).toISOString(),
                effectiveTo: editTaxRule.effectiveTo ? new Date(editTaxRule.effectiveTo).toISOString() : null,
                description: editTaxRule.description || "",
            };

            // Only add taxRuleId for update operations
            if (editTaxRule.taxRuleId) {
                taxRuleData.taxRuleId = editTaxRule.taxRuleId;
            }

            let response;
            if (editTaxRule.taxRuleId) {
                // Update existing tax rule
                response = await apiService.update(
                    config.UPDATETAXRULE,
                    taxRuleData
                );
            } else {
                // Create new tax rule
                response = await apiService.create(
                    config.CREATETAXRULE,
                    taxRuleData
                );
            }

            if (response && (response.success || response === true)) {
                await getTaxRules();
                handleEditClose();
            } else {
                console.error("Failed to save tax rule");
                setFormErrors({ submit: "Failed to save tax rule. Please try again." });
            }
        } catch (error) {
            console.error("Error saving tax rule:", error);
            setFormErrors({
                submit: error.response?.data?.message ||
                    error.response?.data?.title ||
                    "An error occurred while saving"
            });
        } finally {
            setEditBtnLoading(false);
        }
    };

    // Delete functionality
    const handleOpenDeleteModal = (taxRuleId) => {
        setItemToDelete(taxRuleId);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setItemToDelete(null);
    };

    const handleDelete = async () => {
        if (itemToDelete) {
            try {
                await apiService.remove(
                    config.DELETETAXRULE,
                    itemToDelete
                );
                await getTaxRules();
            } catch (error) {
                console.error("Error deleting tax rule:", error);
            }
            handleCloseDeleteModal();
        }
    };

    // Get Global Tax name by ID
    const getGlobalTaxName = (id) => {
        const tax = globalTaxes.find(t => t.globalTaxId === id);
        return tax ? tax.taxName : "N/A";
    };

    // Get Tax Collection name by ID
    const getTaxCollectionName = (id) => {
        if (!id) return "N/A";
        const collection = taxCollections.find(t => t.taxCollectionId === id);
        return collection ? collection.taxName : "N/A";
    };

    // Format date as "12-Aug-2026"
    const formatDate = (dateString) => {
        if (!dateString) return "-";
        try {
            return format(new Date(dateString), "dd-MMM-yyyy");
        } catch {
            return "-";
        }
    };

    // Get badge color for service category
    const getServiceCategoryBadgeColor = (category) => {
        const colors = {
            FLIGHT: "bg-primary",
            HOTEL: "bg-success",
            PACKAGE: "bg-info",
            VISA: "bg-warning",
            BUS: "bg-secondary",
            TRAIN: "bg-danger",
            CAR: "bg-dark",
            TRANSFER: "bg-purple",
            SIGHTSEEING: "bg-teal"
        };
        return colors[category] || "bg-secondary";
    };

    if (authLoading) return <p>Loading...</p>;
    if (!user) {
        navigate("/");
        return null;
    }

    return (
        <>
            <Header />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="card">
                            <div className="p-2">
                                <AccountTabs pageName="TAXRULES" />
                            </div>
                            <div className="card-body">
                                <div className="row mb-4">
                                    <div className="col-12">
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <h5 className="card-title mb-0">
                                                <i className="ri-git-branch-line me-2"></i>
                                                Tax Rules Management
                                            </h5>
                                            <button className="btn btn-primary" onClick={handleAddTaxRule}>
                                                <i className="ri-add-line me-2"></i>
                                                Add Tax Rule
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Info Alert */}
                                <div className="alert alert-info alert-dismissible fade show mb-4">
                                    <i className="ri-information-line me-2"></i>
                                    <strong>Flight Rules:</strong> For Flight service category,
                                    <span className="fw-bold text-danger"> Apply On Base Fare must be FALSE</span> and
                                    <span className="fw-bold text-success"> Apply On Commission must be TRUE</span>.
                                    <button type="button" className="btn-close" data-bs-dismiss="alert"></button>
                                </div>

                                {/* Table Section */}
                                <div className="table-responsive table-card">
                                    <table className="table align-middle table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: "50px" }}>#</th>
                                                <th>Rule Name</th>
                                                <th>Global Tax</th>
                                                <th>TCS Collection</th>
                                                <th>Service Category</th>
                                                <th>Customer Type</th>
                                                <th>Apply On</th>
                                                <th>Effective From</th>
                                                <th>Status</th>
                                                <th style={{ width: "120px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan="10" className="text-center py-4">
                                                        <div className="spinner-border text-primary" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                        <p className="mt-2">Loading tax rules...</p>
                                                    </td>
                                                </tr>
                                            ) : taxRules.length > 0 ? (
                                                taxRules.map((taxRule, index) => (
                                                    <tr key={taxRule.taxRuleId}>
                                                        <td>{index + 1}</td>
                                                        <td>
                                                            <strong>{taxRule.ruleName}</strong>
                                                            {taxRule.description && (
                                                                <div>
                                                                    <small className="text-muted">{taxRule.description}</small>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-primary">
                                                                {getGlobalTaxName(taxRule.globalTaxId)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {taxRule.taxCollectionId ? (
                                                                <span className="badge bg-info">
                                                                    {getTaxCollectionName(taxRule.taxCollectionId)}
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted">N/A</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${getServiceCategoryBadgeColor(taxRule.serviceCategoryId)}`}>
                                                                {taxRule.serviceCategoryId}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-secondary">
                                                                {taxRule.customerType}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex flex-wrap gap-1">
                                                                {taxRule.applyOnBaseFare && (
                                                                    <span className="badge bg-dark">Base Fare</span>
                                                                )}
                                                                {taxRule.applyOnMarkup && (
                                                                    <span className="badge bg-info">Markup</span>
                                                                )}
                                                                {taxRule.applyOnServiceCharge && (
                                                                    <span className="badge bg-warning">Service Charge</span>
                                                                )}
                                                                {taxRule.applyOnCommission && (
                                                                    <span className="badge bg-success">Commission</span>
                                                                )}
                                                                {!taxRule.applyOnBaseFare &&
                                                                    !taxRule.applyOnMarkup &&
                                                                    !taxRule.applyOnServiceCharge &&
                                                                    !taxRule.applyOnCommission && (
                                                                        <span className="text-muted">None</span>
                                                                    )}
                                                            </div>
                                                        </td>
                                                        <td>{formatDate(taxRule.effectiveFrom)}</td>
                                                        <td>
                                                            <span
                                                                className={`badge ${taxRule.isActive ? "bg-success" : "bg-danger"}`}
                                                            >
                                                                {taxRule.isActive ? "Active" : "Inactive"}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <ul className="list-inline hstack gap-2 mb-0">
                                                                <li
                                                                    className="list-inline-item"
                                                                    data-bs-toggle="tooltip"
                                                                    data-bs-trigger="hover"
                                                                    data-bs-placement="top"
                                                                    title="Edit"
                                                                >
                                                                    <Link
                                                                        className="edit-item-btn"
                                                                        onClick={() => handleEditClick(taxRule)}
                                                                    >
                                                                        <i className="ri-pencil-fill align-bottom text-muted" />
                                                                    </Link>
                                                                </li>
                                                                <li
                                                                    className="list-inline-item"
                                                                    data-bs-toggle="tooltip"
                                                                    data-bs-trigger="hover"
                                                                    data-bs-placement="top"
                                                                    title="Delete"
                                                                >
                                                                    <Link
                                                                        className="remove-item-btn"
                                                                        onClick={() =>
                                                                            handleOpenDeleteModal(taxRule.taxRuleId)
                                                                        }
                                                                    >
                                                                        <i className="ri-delete-bin-fill align-bottom text-muted" />
                                                                    </Link>
                                                                </li>
                                                            </ul>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="10" className="text-center py-4">
                                                        <i className="ri-inbox-line fs-1 text-muted d-block mb-2"></i>
                                                        No tax rules found.
                                                        <br />

                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Total count */}
                                <div className="row mt-3">
                                    <div className="col-12">
                                        <span className="text-muted">
                                            Total Tax Rules: {taxRules.length}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add/Edit Tax Rule Modal */}
                <Modal
                    show={editShow}
                    onHide={handleEditClose}
                    size="lg"
                    centered
                    scrollable
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {editTaxRule.taxRuleId ? (
                                <>
                                    <i className="ri-edit-line me-2 text-primary"></i>
                                    Edit Tax Rule - {editTaxRule.ruleName}
                                </>
                            ) : (
                                <>
                                    <i className="ri-add-line me-2 text-primary"></i>
                                    Add New Tax Rule
                                </>
                            )}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                        <form onSubmit={handleSaveTaxRule}>
                            <div className="container-fluid">
                                {/* Error message */}
                                {formErrors.submit && (
                                    <div className="alert alert-danger alert-dismissible fade show mb-3">
                                        <i className="ri-error-warning-line me-2"></i>
                                        {formErrors.submit}
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => setFormErrors({ ...formErrors, submit: null })}
                                        ></button>
                                    </div>
                                )}

                                {/* Flight Validation Warning */}
                                {editTaxRule.serviceCategoryId === "FLIGHT" && (
                                    <div className="alert alert-warning mb-3">
                                        <i className="ri-alert-line me-2"></i>
                                        <strong>Flight Rule Requirements:</strong>
                                        <ul className="mb-0 mt-1">
                                            <li>Apply On Base Fare must be <strong className="text-danger">FALSE</strong></li>
                                            <li>Apply On Commission must be <strong className="text-success">TRUE</strong></li>
                                        </ul>
                                    </div>
                                )}

                                <div className="row">
                                    {/* Rule Name */}
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">
                                            Rule Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${formErrors.ruleName ? "is-invalid" : ""}`}
                                            name="ruleName"
                                            value={editTaxRule.ruleName}
                                            onChange={handleEditInputChange}
                                            placeholder="e.g., India Flight GST Rule"
                                            maxLength={100}
                                        />
                                        {formErrors.ruleName && (
                                            <div className="invalid-feedback">{formErrors.ruleName}</div>
                                        )}
                                    </div>

                                    {/* Global Tax */}
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">
                                            Global Tax <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-select ${formErrors.globalTaxId ? "is-invalid" : ""}`}
                                            name="globalTaxId"
                                            value={editTaxRule.globalTaxId}
                                            onChange={handleSelectChange}
                                        >
                                            <option value="">Select Global Tax</option>
                                            {globalTaxes.map((tax) => (
                                                <option key={tax.globalTaxId} value={tax.globalTaxId}>
                                                    {tax.taxName} ({tax.taxType} - {tax.rate}%)
                                                </option>
                                            ))}
                                        </select>
                                        {formErrors.globalTaxId && (
                                            <div className="invalid-feedback">{formErrors.globalTaxId}</div>
                                        )}
                                    </div>

                                    {/* Tax Collection (TCS) */}
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">TCS Collection</label>
                                        <select
                                            className="form-select"
                                            name="taxCollectionId"
                                            value={editTaxRule.taxCollectionId || ""}
                                            onChange={handleSelectChange}
                                        >
                                            <option value="">None</option>
                                            {taxCollections.map((collection) => (
                                                <option key={collection.taxCollectionId} value={collection.taxCollectionId}>
                                                    {collection.taxName} ({collection.taxType} - {collection.applicableOn})
                                                </option>
                                            ))}
                                        </select>
                                        <small className="text-muted">
                                            Optional - Links to TCS Module
                                        </small>
                                    </div>

                                    {/* Service Category */}
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">
                                            Service Category <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-select ${formErrors.serviceCategoryId ? "is-invalid" : ""}`}
                                            name="serviceCategoryId"
                                            value={editTaxRule.serviceCategoryId}
                                            onChange={handleSelectChange}
                                        >
                                            <option value="">Select Service Category</option>
                                            {serviceCategories.map((category) => (
                                                <option key={category} value={category}>
                                                    {category}
                                                </option>
                                            ))}
                                        </select>
                                        {formErrors.serviceCategoryId && (
                                            <div className="invalid-feedback">{formErrors.serviceCategoryId}</div>
                                        )}
                                    </div>

                                    {/* Customer Type */}
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">
                                            Customer Type <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-select ${formErrors.customerType ? "is-invalid" : ""}`}
                                            name="customerType"
                                            value={editTaxRule.customerType}
                                            onChange={handleSelectChange}
                                        >
                                            <option value="">Select Customer Type</option>
                                            {customerTypes.map((type) => (
                                                <option key={type} value={type}>
                                                    {type}
                                                </option>
                                            ))}
                                        </select>
                                        {formErrors.customerType && (
                                            <div className="invalid-feedback">{formErrors.customerType}</div>
                                        )}
                                    </div>

                                    {/* Apply On - Section Header */}
                                    <div className="col-12 mb-3 mt-2">
                                        <h6 className="border-bottom pb-2 text-primary fw-bold">
                                            <i className="ri-checkbox-multiple-line me-2"></i>
                                            Apply On
                                        </h6>
                                    </div>

                                    {/* Apply On Base Fare */}
                                    <div className="col-md-3 mb-3">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="applyOnBaseFare"
                                                name="applyOnBaseFare"
                                                checked={editTaxRule.applyOnBaseFare}
                                                onChange={handleEditInputChange}
                                                disabled={editTaxRule.serviceCategoryId === "FLIGHT"}
                                            />
                                            <label className="form-check-label" htmlFor="applyOnBaseFare">
                                                Base Fare
                                            </label>
                                        </div>
                                        {editTaxRule.serviceCategoryId === "FLIGHT" && (
                                            <small className="text-danger d-block">Must be FALSE for Flights</small>
                                        )}
                                        {formErrors.applyOnBaseFare && (
                                            <div className="text-danger small">{formErrors.applyOnBaseFare}</div>
                                        )}
                                    </div>

                                    {/* Apply On Markup */}
                                    <div className="col-md-3 mb-3">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="applyOnMarkup"
                                                name="applyOnMarkup"
                                                checked={editTaxRule.applyOnMarkup}
                                                onChange={handleEditInputChange}
                                            />
                                            <label className="form-check-label" htmlFor="applyOnMarkup">
                                                Markup
                                            </label>
                                        </div>
                                    </div>

                                    {/* Apply On Service Charge */}
                                    <div className="col-md-3 mb-3">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="applyOnServiceCharge"
                                                name="applyOnServiceCharge"
                                                checked={editTaxRule.applyOnServiceCharge}
                                                onChange={handleEditInputChange}
                                            />
                                            <label className="form-check-label" htmlFor="applyOnServiceCharge">
                                                Service Charge
                                            </label>
                                        </div>
                                    </div>

                                    {/* Apply On Commission */}
                                    <div className="col-md-3 mb-3">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="applyOnCommission"
                                                name="applyOnCommission"
                                                checked={editTaxRule.applyOnCommission}
                                                onChange={handleEditInputChange}
                                                disabled={editTaxRule.serviceCategoryId === "FLIGHT"}
                                            />
                                            <label className="form-check-label" htmlFor="applyOnCommission">
                                                Commission
                                            </label>
                                        </div>
                                        {editTaxRule.serviceCategoryId === "FLIGHT" && (
                                            <small className="text-success d-block">Must be TRUE for Flights</small>
                                        )}
                                        {formErrors.applyOnCommission && (
                                            <div className="text-danger small">{formErrors.applyOnCommission}</div>
                                        )}
                                    </div>

                                    {/* Status */}
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">
                                            Status <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-select ${formErrors.isActive ? "is-invalid" : ""}`}
                                            name="isActive"
                                            value={editTaxRule.isActive.toString()}
                                            onChange={handleSelectChange}
                                        >
                                            <option value="true">Active</option>
                                            <option value="false">Inactive</option>
                                        </select>
                                        {formErrors.isActive && (
                                            <div className="invalid-feedback">{formErrors.isActive}</div>
                                        )}
                                    </div>

                                    {/* Effective From */}
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">
                                            Effective From <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="datetime-local"
                                            className={`form-control ${formErrors.effectiveFrom ? "is-invalid" : ""}`}
                                            name="effectiveFrom"
                                            value={editTaxRule.effectiveFrom}
                                            onChange={handleEditInputChange}
                                        />
                                        {formErrors.effectiveFrom && (
                                            <div className="invalid-feedback">{formErrors.effectiveFrom}</div>
                                        )}
                                    </div>

                                    {/* Effective To */}
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">Effective To</label>
                                        <input
                                            type="datetime-local"
                                            className={`form-control ${formErrors.effectiveTo ? "is-invalid" : ""}`}
                                            name="effectiveTo"
                                            value={editTaxRule.effectiveTo || ""}
                                            onChange={handleEditInputChange}
                                        />
                                        <small className="text-muted">Optional - Leave empty for indefinite</small>
                                        {formErrors.effectiveTo && (
                                            <div className="invalid-feedback">{formErrors.effectiveTo}</div>
                                        )}
                                    </div>

                                    {/* Description */}
                                    <div className="col-12 mb-3">
                                        <label className="form-label fw-bold">Description</label>
                                        <textarea
                                            className="form-control"
                                            name="description"
                                            value={editTaxRule.description || ""}
                                            onChange={handleEditInputChange}
                                            placeholder="Additional notes about this tax rule"
                                            rows={2}
                                            maxLength={500}
                                        />
                                    </div>
                                </div>

                                {/* Form Actions */}
                                <div className="row mt-3">
                                    <div className="col-12">
                                        <hr />
                                        <div className="d-flex justify-content-end gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-danger"
                                                onClick={handleEditClose}
                                                disabled={editBtnLoading}
                                            >
                                                <i className="ri-close-line me-1"></i>
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-primary"
                                                disabled={editBtnLoading}
                                            >
                                                {editBtnLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                        {editTaxRule.taxRuleId ? "Updating..." : "Creating..."}
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className={`ri-${editTaxRule.taxRuleId ? "save" : "add"}-line me-1`}></i>
                                                        {editTaxRule.taxRuleId ? "Update Tax Rule" : "Create Tax Rule"}
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </Modal.Body>
                </Modal>

                {/* Delete Confirmation Modal */}
                <DeleteAlert
                    show={showDeleteModal}
                    handleClose={handleCloseDeleteModal}
                    handleDelete={handleDelete}
                    title="Delete Tax Rule"
                    message="Are you sure you want to delete this tax rule? This action cannot be undone."
                />
            </div>
        </>
    );
};

export default TaxRules;